import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { BrandTask, getMyBrandTasks } from '../../api/payment/brand-deal.api';
import { countdownLabel, creativeRulesLabel, rejectReasonLabel, taskStatusLabel } from '../../utils/brand-creative';
import { daysLeftLabel, disputeStatusLabel } from '../../utils/brand-dispute';
import { routePaths } from '../../routes/routes-path';
import { IndividualOrdererBadge } from './orderer-badge';
import TurnPill from './turn-pill';
import { taskTurn } from '../../utils/brand-turn';

/**
 * Tab "Việc của tôi" (creator, M3 Task 6): mọi nhiệm vụ brief sáng tạo của creator, mọi trạng thái — hạn,
 * trạng thái, ghi chú cần sửa, lý do từ chối. Bấm 1 dòng ⇒ mở bàn soạn bài (`/collab/task/{id}`).
 */

const WORKING_STATUSES = ['Working', 'RevisionRequested'];

// Khai ngoài component cha (landmine ghi nhớ: component khai trong render body gây remount cả subtree).
const TaskRow: React.FC<{ task: BrandTask; nowMs: number; onOpen: () => void }> = ({ task, nowMs, onOpen }) => {
  const deadline = WORKING_STATUSES.includes(task.status) ? countdownLabel(task.submitDeadline, nowMs) : '';
  const publishDeadline = task.status === 'AwaitingPublish' ? countdownLabel(task.publishDueAt, nowMs) : '';

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full flex-col gap-1 p-4 text-left text-[14px]"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="ui-heading font-bold">{task.title}</span>
        <span className="flex items-center gap-2">
          <span className="ui-muted">{task.brandName}</span>
          <IndividualOrdererBadge kind={task.ordererKind} />
        </span>
      </div>
      <div className="ui-muted flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[13px]">
        <TurnPill turn={taskTurn(task.status)} />
        <span>{taskStatusLabel(task.status)}</span>
        {deadline && <span>{deadline}</span>}
        {publishDeadline && <span>Hạn đăng: {publishDeadline}</span>}
        <span>{creativeRulesLabel(task.maxRevisions, task.reviewDays)}</span>
        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
          {Number(task.netVnd || 0).toLocaleString('vi-VN')}đ
        </span>
      </div>
      {task.status === 'RevisionRequested' && task.revisionNote && (
        <p className="rounded-lg bg-amber-50 p-2 text-[13px] text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          Doanh nghiệp muốn bạn sửa bài: {task.revisionNote}
        </p>
      )}
      {task.status === 'Rejected' && task.rejectReasonCode && (
        <p className="rounded-lg bg-rose-50 p-2 text-[13px] text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
          Doanh nghiệp đã từ chối bài: {rejectReasonLabel(task.rejectReasonCode)}
          {task.rejectReasonText ? ` — ${task.rejectReasonText}` : ''}
        </p>
      )}
      {/* Fix round 1 (review Important #2): disputeStatus nay đã có ở danh sách creator — phân biệt được
          "chưa kháng cáo" (còn hạn ⇒ vẫn hiện CTA đếm ngày) với "đã kháng cáo" (hiện đúng trạng thái,
          không còn hiện "Còn X ngày" nữa vì đã gửi rồi). */}
      {task.status === 'Rejected' && task.disputeStatus && (
        <p className="text-[12px] font-semibold text-amber-600 dark:text-amber-400">
          Kháng cáo: {disputeStatusLabel(task.disputeStatus)}
        </p>
      )}
      {task.status === 'Rejected' &&
        !task.disputeStatus &&
        task.disputeWindowEndsAt &&
        Date.parse(task.disputeWindowEndsAt) > nowMs && (
          <p className="text-[12px] font-semibold text-amber-600 dark:text-amber-400">
            {daysLeftLabel(task.disputeWindowEndsAt, nowMs)}
          </p>
        )}
    </button>
  );
};

const BrandTaskList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<BrandTask[]>([]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    getMyBrandTasks()
      .then(setTasks)
      .catch(() => toast.error('Không tải được danh sách nhiệm vụ.'))
      .finally(() => setLoading(false));
  }, []);

  // Cập nhật đồng hồ đếm hạn mỗi phút — không cần chính xác tới giây ở màn danh sách.
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const openTask = (task: BrandTask) => navigate(`${routePaths.brandDeals}/task/${task.dealId}`);

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="animate-spin ui-muted" />
      </div>
    );
  }

  if (tasks.length === 0) {
    return <p className="ui-muted p-4 text-center text-[14px]">Bạn chưa nhận brief sáng tạo nào.</p>;
  }

  return (
    <div className="ui-card divide-y ui-divider">
      {tasks.map((task) => (
        <TaskRow key={task.dealId} task={task} nowMs={nowMs} onOpen={() => openTask(task)} />
      ))}
    </div>
  );
};

export default BrandTaskList;
